import React, { useContext, useMemo } from 'react';
import get from 'lodash/get';

import { EndpointTypeSetting } from '../hooks/helpers';
import FormContext from 'src/presentation/contexts/FormContext';
import {
  EntryPointItemRow,
  StyledMultiSelectDropdown,
} from 'src/presentation/components/pages/channels/entryPoints/components/EntryPointLayouts';
import { includeHierarchicalOptions } from 'src/presentation/components/pages/channels/entryPoints/hooks/helpers';

import MinutesInputField, { TimeUnits } from 'src/presentation/components/pages/shared/MinutesInputField';
import RoutingGroup from 'src/models/RoutingGroup/RoutingGroup';
import RoutingGroupDropDown from 'src/presentation/components/pages/channels/entryPoints/components/RoutingGroupDropDown';
import useDefaultSlaMinutes from 'src/presentation/components/pages/channels/entryPoints/hooks/useDefaultSlaMinutes';

export type InboxesMapType = Record<string, RoutingGroup>;

type EntryPointExceptionItemType = {
  endpoints: { label: string; value: string }[];
  inboxes: { label: string; value: string }[];
  inboxesMap: InboxesMapType;
  index: number;
  onSelect: (index: number, changedValue: string[]) => void;
};

export default function EntryPointExceptionItem({
  endpoints,
  inboxes,
  inboxesMap,
  index,
  onSelect,
}: EntryPointExceptionItemType): JSX.Element | null {
  const { workingState } = useContext(FormContext);

  const defaultSlaMinutes = useDefaultSlaMinutes({ baseAttr: `exceptionsGrouped[${index}]`, inboxesMap });
  const exceptionsGrouped = (workingState as EndpointTypeSetting)?.exceptionsGrouped ?? [];
  const defaultsIds = useMemo(() => (workingState as EndpointTypeSetting)?.defaultsIds ?? [], [workingState]);
  const currentGroup: any = get(workingState, `exceptionsGrouped[${index}]`);

  const availableCommunicationQueues = useMemo(() => {
    const includeIds: string[] = []; // IDs of the queues which belong to another groups
    includeIds.push(...currentGroup.communicationQueues);
    includeIds.push(...defaultsIds);

    return includeHierarchicalOptions({ items: endpoints, includeIds });
  }, [currentGroup, endpoints, defaultsIds]);

  const handleSelect = changedValue => {
    onSelect(index, changedValue);
  };

  const endpointControl = (
    <StyledMultiSelectDropdown
      attr={`exceptionsGrouped[${index}].communicationQueues`}
      className="multi-select-dropdown"
      data-aid={`exceptionsGrouped[${index}]-communicationQueues`}
      entityNamePlural=""
      onSelect={handleSelect}
      options={availableCommunicationQueues}
      placeholder="Entry Point"
    />
  );

  if (!exceptionsGrouped.length) {
    return null;
  }

  return (
    <EntryPointItemRow
      entryPointElement={endpointControl}
      inboxElement={
        <RoutingGroupDropDown
          baseAttr={`exceptionsGrouped[${index}]`}
          className="inbox-dropdown"
          inboxes={inboxes}
          inboxesMap={inboxesMap}
        />
      }
      slaElement={
        <MinutesInputField
          allowDecimalDigits={true}
          attr={`exceptionsGrouped[${index}].slaSeconds`}
          data-aid={`exceptionsGrouped[${index}].slaSeconds`}
          displayUnits={TimeUnits.Minutes}
          sourceUnits={TimeUnits.Seconds}
          placeholder={defaultSlaMinutes.toString()}
        />
      }
    />
  );
}
